export const MAX_AUTO_RESUME_ATTEMPTS = 3;

export type ResumeDecision = { action: 'retry'; attempt: number } | { action: 'give-up' };

export interface ResumeDecisionInput {
  attempts: number;
}

export type ResumeErrorClass = 'permission-denied' | 'retry';

interface NameBearing {
  name?: unknown;
}

/**
 * Classify a getUserMedia / MediaRecorder failure encountered during auto-resume.
 *
 * - permission-denied → user revoked mic mid-recording; do NOT retry, surface
 *   immediately so the UI can prompt them. Covers `NotAllowedError` (spec name),
 *   `PermissionDeniedError` (legacy pre-spec name still thrown by older Firefox),
 *   and `SecurityError` (insecure context / blocked by policy).
 * - retry → transient (mic busy from another tab, hardware glitch); retry
 *   subject to the budget.
 */
export function classifyResumeError(err: unknown): ResumeErrorClass {
  if (typeof err === 'object' && err !== null) {
    const name = (err as NameBearing).name;
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return 'permission-denied';
    }
  }
  return 'retry';
}

/**
 * Given the number of consecutive auto-resume failures so far, decide what to
 * do next. `attempts` is the count BEFORE this decision; the returned
 * `attempt` number is what the new attempt would be (1-indexed).
 */
export function nextResumeDecision(input: ResumeDecisionInput): ResumeDecision {
  if (input.attempts >= MAX_AUTO_RESUME_ATTEMPTS) {
    return { action: 'give-up' };
  }
  return { action: 'retry', attempt: input.attempts + 1 };
}
